<template>
  <b-container fluid>
    <div id="sidebar" class="leaflet-sidebar collapsed">
      <!-- Nav tabs -->
      <div class="leaflet-sidebar-tabs">
        <ul role="tablist">
          <!-- top aligned tabs -->
          <li>
            <a href="#home" role="tab"
              ><font-awesome-icon :icon="['fas', 'home']"
            /></a>
          </li>
          <li v-show="!user.external_user">
            <a href="#add" role="tab"
              ><font-awesome-icon :icon="['fas', 'plus']"
            /></a>
          </li>
          <li v-for="day in days" :key="'day' + day.id">
            <a :href="'#' + day.short_name.toLowerCase()" role="tab">
              <font-awesome-icon
                v-if="day.icon"
                :icon="['fas', getIconForSidebarTab(day)]"
              />
              <b v-else>{{ day.short_name.toUpperCase() }}</b>
            </a>
          </li>
        </ul>

        <ul role="tablist">
          <!-- bottom aligned tabs -->
          <li>
            <a href="#profile" role="tab"
              ><font-awesome-icon :icon="['fas', 'user']"
            /></a>
          </li>
        </ul>
      </div>

      <!-- Tab panes -->
      <div class="leaflet-sidebar-content">
        <div class="leaflet-sidebar-pane" id="home">
          <h1 class="leaflet-sidebar-header">
            OpenRaideur
            <div class="leaflet-sidebar-close">
              <font-awesome-icon :icon="['fas', 'caret-left']" />
            </div>
          </h1>
          <h3>Bienvenue sur OpenRaideur !</h3>
          <p style="text-align: justify">
            OpenRaideur est un outil destiné à aider l'équipe du Raid INSA Lyon
            à préparer ses manifestations. Il est inspiré de MyMaps et de
            Openrunner, et utilise des fonds de carte de l'IGN. Son code source
            est disponible sur
            <b-link
              href="https://gitlab.com/sia-insa-lyon/BdEINSALyon/openraideur"
              target="_blank"
              >GitLab</b-link
            >.
          </p>
          <p style="text-align: justify" v-show="!user.external_user">
            En tant que membre de l'équipe du Raid, n'hésitez pas à me faire
            part de vos remarques ou suggestions de vive voix, en créant
            <b-link
              href="https://gitlab.com/sia-insa-lyon/BdEINSALyon/openraideur/-/issues"
              target="_blank"
              >une issue sur GitLab</b-link
            >
            ou en complétant
            <b-link
              href="https://bdeinsalyon-my.sharepoint.com/:w:/g/personal/guillaume_cocault_bde-insa-lyon_fr/ETDLVJ7DX1tJvBYToJQTvZQBd5V32AUwncbIZbRkKlKi6g"
              target="_blank"
              >ce document</b-link
            >.
          </p>
          <p style="text-align: justify" v-show="!user.external_user">
            La liste des versions, des nouveautés et des corrections de bugs est
            disponible sur
            <b-link
              href="https://gitlab.com/sia-insa-lyon/BdEINSALyon/openraideur/-/releases"
              target="_blank"
              >cette page GitLab</b-link
            >.
          </p>
          <b-img center :src="require('@/assets/RAID-fond-blanc.png')"></b-img>
        </div>
        <div class="leaflet-sidebar-pane" id="add" v-show="!user.external_user">
          <h1 class="leaflet-sidebar-header">
            Ajouter
            <div class="leaflet-sidebar-close">
              <font-awesome-icon :icon="['fas', 'caret-left']" />
            </div>
          </h1>
          <div role="tablist">
            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.accordion-add-gpx variant="secondary"
                  >Importer un GPX</b-button
                >
              </b-card-header>
              <b-collapse
                id="accordion-add-gpx"
                accordion="accordion-add"
                role="tabpanel"
              >
                <b-card-body>
                  <b-card-text>
                    <p>Cette option permet d'ajouter un parcours ou un marqueur à partir d'un fichier GPX.</p>
                    <b-form-file
                      accept=".gpx"
                      v-model="gpxFile"
                      @input="onGPXFileChoose"
                      placeholder="Choisir un fichier GPX à importer..."
                    ></b-form-file>
                  </b-card-text>
                </b-card-body>
              </b-collapse>
            </b-card>

            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.accordion-add-kml variant="secondary"
                  >Importer une journée (KML)</b-button
                >
              </b-card-header>
              <b-collapse
                id="accordion-add-kml"
                accordion="accordion-add"
                role="tabpanel"
              >
                <b-card-body>
                  <b-card-text>
                    <p>Cette fonctionnalité permet d'importer une journée entière depuis un fichier KML généré par MyMaps</p>
                    <b-form-file
                      accept=".kml"
                      v-model="kmlFile"
                      @input="onKMLFileChoose"
                      placeholder="Choisir un fichier KML à importer..."
                    ></b-form-file>
                  </b-card-text>
                </b-card-body>
              </b-collapse>
            </b-card>

            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button
                  block
                  v-b-toggle.accordion-add-marker
                  variant="secondary"
                  >Ajouter un marqueur</b-button
                >
              </b-card-header>
              <b-collapse
                id="accordion-add-marker"
                accordion="accordion-add"
                role="tabpanel"
                v-on:show="onWaypointAddStarted"
                v-on:hide="onWaypointAddCanceled"
              >
                <b-card-body>
                  <b-card-text>
                    <b-form @submit="onNewWaypointSave" autocomplete="off">
                      <b-form-group
                        id="input-group-addmarker-1"
                        label="Nom du marqueur :"
                        label-for="input-addmarker-1"
                      >
                        <b-form-input
                          id="input-addmarker-1"
                          v-model="newFeature.name"
                          required
                          placeholder="Entrez le nom du marqueur"
                        ></b-form-input>
                      </b-form-group>

                      <b-form-group
                        id="input-group-addmarker-2"
                        label="Couche :"
                        label-for="input-addmarker-2"
                      >
                        <b-form-select
                          id="input-addmarker-2"
                          v-model="newFeature.layer"
                          required
                          placeholder="Choissisez la couche"
                          :options="getLayerOptions"
                        ></b-form-select>
                      </b-form-group>

                      <b-form-group
                        id="input-group-addmarker-3"
                        label="Couleur :"
                        label-for="input-addmarker-3"
                      >
                        <b-form-input
                          id="input-addmarker-3"
                          v-model="newFeature.color"
                          required
                          type="color"
                          list
                          value="#FF0000"
                          placeholder="Choissez une couleur"
                        ></b-form-input>
                      </b-form-group>

                      <b-form-group
                        id="input-group-addmarker-4"
                        label="Icône :"
                        label-for="input-addmarker-4"
                      >
                        <vfa-picker v-model="newFeature.icon" :only="['solid']">
                          <template v-slot:activator="{ on }">
                            <b-form-input
                              id="input-addmarker-4"
                              v-model="newFeature.icon"
                              required
                              @click="on"
                              type="text"
                              placeholder="Choissiez une icône..."
                            ></b-form-input>
                          </template>
                        </vfa-picker>
                      </b-form-group>

                      <b-button type="submit" variant="success"
                        >Enregistrer</b-button
                      >
                    </b-form>
                  </b-card-text>
                </b-card-body>
              </b-collapse>
            </b-card>

            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button
                  block
                  v-b-toggle.accordion-add-race
                  variant="secondary"
                  >Ajouter un parcours</b-button
                >
              </b-card-header>
              <b-collapse
                id="accordion-add-race"
                accordion="accordion-add"
                role="tabpanel"
                v-on:show="onRaceAddStarted"
                v-on:hide="onRaceAddCanceled"
              >
                <b-card-body>
                  <b-card-text>
                    <b-form @submit="onNewRaceSave" autocomplete="off">
                      <b-form-group
                        id="input-group-addrace-1"
                        label="Nom du parcours :"
                        label-for="input-addrace-1"
                      >
                        <b-form-input
                          id="input-addrace-1"
                          v-model="newFeature.name"
                          required
                          placeholder="Entrez le nom du parcours"
                        ></b-form-input>
                      </b-form-group>

                      <b-form-group
                        id="input-group-addrace-2"
                        label="Couche :"
                        label-for="input-addrace-2"
                      >
                        <b-form-select
                          id="input-addrace-2"
                          v-model="newFeature.layer"
                          required
                          placeholder="Choissisez la couche"
                          :options="getLayerOptions"
                        ></b-form-select>
                      </b-form-group>

                      <b-form-group
                        id="input-group-addrace-3"
                        label="Couleur :"
                        label-for="input-addrace-3"
                      >
                        <b-form-input
                          id="input-addrace-3"
                          v-model="newFeature.color"
                          required
                          type="color"
                          list
                          value="#FF0000"
                          placeholder="Choissez une couleur"
                        ></b-form-input>
                      </b-form-group>

                      <b-button type="submit" variant="success"
                        >Enregistrer</b-button
                      >
                    </b-form>
                  </b-card-text>
                </b-card-body>
              </b-collapse>
            </b-card>

            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button
                  block
                  v-b-toggle.accordion-add-layer
                  variant="secondary"
                  >Ajouter un calque</b-button
                >
              </b-card-header>
              <b-collapse
                id="accordion-add-layer"
                accordion="accordion-add"
                role="tabpanel"
              >
                <b-card-body>
                  <b-card-text>
                    <b-form @submit="onNewLayerSave" autocomplete="off">
                      <b-form-group
                        id="input-group-addlayer-1"
                        label="Nom du calque :"
                        label-for="input-addlayer-1"
                      >
                        <b-form-input
                          id="input-addlayer-1"
                          v-model="newLayer.name"
                          required
                          placeholder="Entrez le nom du calque"
                        ></b-form-input>
                      </b-form-group>

                      <b-form-group
                        id="input-group-addlayer-2"
                        label="Jour :"
                        label-for="input-addlayer-2"
                      >
                        <b-form-select
                          id="input-addlayer-2"
                          v-model="newLayer.day"
                          required
                          placeholder="Choissisez le jour"
                          :options="days"
                          value-field="id"
                          text-field="name"
                        ></b-form-select>
                      </b-form-group>

                      <b-button type="submit" variant="success"
                        >Enregistrer</b-button
                      >
                    </b-form>
                  </b-card-text>
                </b-card-body>
              </b-collapse>
            </b-card>
          </div>
        </div>
        <div
          class="leaflet-sidebar-pane"
          :id="day.short_name.toLowerCase()"
          v-for="day in days"
          :key="'day' + day.id"
        >
          <h1 class="leaflet-sidebar-header">
            {{ day.name }}
            <b-link
              variant="link"
              @click="downloadKML(day.name, day.id)"
              v-b-tooltip.hover
              title="Télécharger le KML"
            >
              <font-awesome-icon
                class="btn-primary"
                :icon="['fas', 'file-download']"
              ></font-awesome-icon>
            </b-link>
            <div class="leaflet-sidebar-close">
              <font-awesome-icon :icon="['fas', 'caret-left']" />
            </div>
          </h1>
          <LayerPanel
            v-for="layer in getLayersForDay(day.id)"
            :key="layer.id"
            v-on:layer-visibility-changed="onLayerVisibilityChanged"
            v-on:feature-clicked="onFeatureSelected"
            v-on:toggle-ant-race="onToggleAntRace"
            v-on:fly-to-feature="onFlyToFeatureClick"
            :linked-layer="layer"
          />
        </div>
        <div class="leaflet-sidebar-pane" id="profile">
          <h1 class="leaflet-sidebar-header">
            Profil
            <div class="leaflet-sidebar-close">
              <font-awesome-icon :icon="['fas', 'caret-left']" />
            </div>
          </h1>
          <h2>
            {{ user.username }}
            <b-button variant="danger" @click="logout">
              <font-awesome-icon :icon="['fas', 'sign-out-alt']" />
              Se déconnecter
            </b-button>
          </h2>
          <p>
            Vous utilisez le compte <b>{{ user.username }}</b
            ><span v-show="user.external_user"
              >, identifié comme un utilisateur externe au BdE INSA Lyon</span
            >.
          </p>
          <p>
            Vous avez accès aux données de l'édition <b>{{ user.year }}</b> du
            Raid INSA Lyon - Orange ou du Raid Dingue de Villeurbanne.
          </p>
          <p>
            Les informations contenues sur ce site sont strictement
            confidentielles. Elles ne doivent pas être diffusées, sous quelque
            forme que ce soit, sans l'accord écrit du BdE INSA Lyon.
          </p>

          <br />
          <b-link
            href="/api/admin/"
            target="_blank"
            v-show="!user.external_user"
          >
            <font-awesome-icon :icon="['fas', 'user-lock']" />
            Accès à la console d'administration
          </b-link>
        </div>
      </div>
    </div>

    <div id="leaflet-map"></div>
    <div id="polyline-tooltip"></div>

    <FeatureDetails
      v-if="
        selectedFeature.type == 'race' || selectedFeature.type == 'waypoint'
      "
      :feature.sync="selectedFeature"
      :disciplines="disciplines"
      :layer-options="getLayerOptions"
      :previous-races="getSameDayRaces"
      :levels="levels"
      :external-user="user.external_user"
      @modal-feature-changed="modalFeatureChanged"
      @save-error="modalSaveError"
      @delete-success="modalDeleteSuccess"
      @delete-error="modalDeleteError"
      @toggle-selected-feature-edit-state="toggleSelectedFeatureEditState"
    />
  </b-container>
</template>

<script>
import "leaflet/dist/leaflet.css";
var L = require("leaflet");

require("leaflet-editable");
require("leaflet-editable-polyline");

import "@raruto/leaflet-elevation/dist/leaflet-elevation.min.css";
require("@raruto/leaflet-elevation");

import "leaflet-sidebar-v2/css/leaflet-sidebar.min.css";
require("leaflet-sidebar-v2");

import { AntPath } from "leaflet-ant-path";

import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
library.add(fas);

import { dom } from "@fortawesome/fontawesome-svg-core";

dom.watch();

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const axios = require("axios").default;

import LayerPanel from "./LayerPanel";
import FeatureDetails from "./FeatureDetails";

export default {
  name: "Map",
  components: {
    FontAwesomeIcon,
    LayerPanel,
    FeatureDetails,
  },
  data() {
    return {
      map: null,
      sidebar: null,
      elevationControl: null,
      selectedFeature: Object,
      layers: [],
      days: [],
      years: [],
      gpxFile: null,
      kmlFile: null,
      newFeature: {
        name: "",
        color: "#FF0000",
        drawnDistance: 0,
        additionnalDistance: 0,
      },
      antRace: null,
      polylineTooltip: null,
      newLayer: {
        name: "",
        year: null,
        day: null,
      },
      disciplines: [],
      levels: [],
      user: Object,
    };
  },
  computed: {
    getSameDayRaces() {
      let dayId = this.layers.find((l) => l.id == this.selectedFeature.layer)
        .day.id;
      let layers = this.layers.filter((l) => l.day.id == dayId);

      let races = [];
      for (let l of layers) {
        let opts = [];
        for (let r of l.openraideur_race_features) {
          if (r.id != this.selectedFeature.id) {
            opts.push({ value: r.id, text: r.name });
          }
        }
        races.push({ label: l.name, options: opts });
      }

      return races;
    },
    getLayerOptions() {
      let opts = [];
      this.layers.forEach((l) => {
        let dayItem = opts.find((x) => x.label == l.day.name);
        if (!dayItem) {
          dayItem = {
            label: l.day.name,
            options: [],
          };
          opts.push(dayItem);
        }
        dayItem.options.push({
          value: l.id,
          text: l.name,
        });
      });
      return opts;
    },
  },
  async mounted() {
    window.onbeforeunload = () =>
      this.layers.some(
        (l) =>
          l.openraideur_race_features.some((f) => f.editState == true) ||
          l.openraideur_waypoint_features.some((f) => f.editState == true)
      )
        ? true
        : null;

    this.user = {
      username: localStorage.getItem("username"),
      year: localStorage.getItem("year"),
      external_user: localStorage.getItem("external_user") == "true",
    };

    await axios
      .get("/api/day/")
      .then((response) => {
        this.days = response.data;
      })
      .catch((error) => {
        this.showToast(
          "danger",
          "Erreur de récupération des données",
          "Impossible de récupérer les données des journées depuis le serveur (" +
            error +
            ")."
        );
      });

    this.initMap();

    axios
      .get("/api/layer/")
      .then((response) => {
        this.layers = response.data;
        this.initLayers();
      })
      .catch((error) => {
        this.showToast(
          "danger",
          "Erreur de récupération des données",
          "Impossible de récupérer les données des couches depuis le serveur (" +
            error +
            ")."
        );
      });

    this.updateYearList();
    this.updateDisciplineList();
    this.updateLevelList();
  },
  methods: {
    showSaveSuccessToast(feature) {
      this.showToast(
        "success",
        "Enregistrement terminé",
        feature.name + " a été enregistré avec succès."
      );
    },
    showSaveErrorToast(feature, error) {
      this.showToast(
        "danger",
        "Erreur d'enregistrement",
        "Impossible d'enregistrer " + feature.name + " (" + error + ")."
      );
    },
    showToast(variant, title, body) {
      this.$bvToast.toast(body, {
        title: title,
        variant: variant,
        solid: true,
      });
    },
    getIconForSidebarTab(day) {
      return day.icon;
    },
    async getRaceTrack(race) {
      await axios
        .get(`/api/race/${race.id}/?fields=track`)
        .then((response) => {
          race.track = response.data.track;
        })
        .catch((error) => {
          this.showToast(
            "danger",
            "Erreur de récupération des données",
            "Impossible de télécharger le parcours de " +
              race.name +
              " (" +
              error +
              ")."
          );
        });
    },
    async updateDisciplineList() {
      await axios
        .get(`/api/discipline/`)
        .then((response) => {
          this.disciplines = response.data;
        })
        .catch((error) => {
          this.showToast(
            "danger",
            "Erreur de récupération des données",
            "Impossible de télécharger la liste des disciplines (" +
              error +
              ")."
          );
        });
    },
    async updateLevelList() {
      await axios
        .get(`/api/level/`)
        .then((response) => {
          this.levels = response.data;
        })
        .catch((error) => {
          this.showToast(
            "danger",
            "Erreur de récupération des données",
            "Impossible de télécharger la liste des niveaux (" + error + ")."
          );
        });
    },
    async updateYearList() {
      await axios
        .get(`/api/year/`)
        .then((response) => {
          this.years = response.data;
        })
        .catch((error) => {
          this.showToast(
            "danger",
            "Erreur de récupération des données",
            "Impossible de télécharger la liste des années (" + error + ")."
          );
        });
    },
    getLayersForDay(dayId) {
      return this.layers.filter((l) => l.day.id == dayId);
    },
    initMap() {

      let planIGN = L.tileLayer(
        "https://data.geopf.fr/wmts?" +
          "&REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=PM" +
          "&LAYER={ignLayer}&STYLE={style}&FORMAT={format}" +
          "&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}",
        {
          ignLayer: "GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2",
          style: "normal",
          format: "image/png",
          service: "WMTS",
          attribution:
            '&copy; <a href="http://www.ign.fr/" target="_blank">IGN</a>, &copy; <a href="http://raid.bde-insa-lyon.fr/" target="_blank">Raid INSA Lyon - Orange</a>',
        }
      );

      let carteIGN = L.tileLayer(
        "https://data.geopf.fr/wmts?" +
          "&REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=PM" +
          "&LAYER={ignLayer}&STYLE={style}&FORMAT={format}" +
          "&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}",
        {
          ignLayer: "GEOGRAPHICALGRIDSYSTEMS.MAPS",
          style: "normal",
          format: "image/jpeg",
          service: "WMTS",
          attribution:
            '&copy; <a href="http://www.ign.fr/" target="_blank">IGN</a>, &copy; <a href="http://raid.bde-insa-lyon.fr/" target="_blank">Raid INSA Lyon - Orange</a>',
        }
      );

      let photosAeriennes = L.tileLayer(
        "https://data.geopf.fr/wmts?" +
          "&REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=PM" +
          "&LAYER={ignLayer}&STYLE={style}&FORMAT={format}" +
          "&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}",
        {
          ignLayer: "ORTHOIMAGERY.ORTHOPHOTOS",
          style: "normal",
          format: "image/jpeg",
          service: "WMTS",
          attribution:
            '&copy; <a href="http://www.ign.fr/" target="_blank">IGN</a>, &copy; <a href="http://raid.bde-insa-lyon.fr/" target="_blank">Raid INSA Lyon - Orange</a>',
        }
      );

      let natura2000Habitats = L.tileLayer(
        "https://data.geopf.fr/wmts?" +
          "&REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=PM" +
          "&LAYER={ignLayer}&STYLE={style}&FORMAT={format}" +
          "&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}",
        {
          ignLayer: "PROTECTEDAREAS.SIC",
          style: "normal",
          format: "image/png",
          service: "WMTS",
          opacity: 0.6,
          attribution:
            '&copy; <a href="http://www.ign.fr/" target="_blank">IGN</a>, &copy; <a href="http://raid.bde-insa-lyon.fr/" target="_blank">Raid INSA Lyon - Orange</a>',
        }
      );

      let natura2000Oiseaux = L.tileLayer(
        "https://data.geopf.fr/wmts?" +
          "&REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=PM" +
          "&LAYER={ignLayer}&STYLE={style}&FORMAT={format}" +
          "&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}",
        {
          ignLayer: "PROTECTEDAREAS.ZPS",
          style: "normal",
          format: "image/png",
          service: "WMTS",
          opacity: 0.6,
          attribution:
            '&copy; <a href="http://www.ign.fr/" target="_blank">IGN</a>, &copy; <a href="http://raid.bde-insa-lyon.fr/" target="_blank">Raid INSA Lyon - Orange</a>',
        }
      );

      let cadastre = L.tileLayer(
        "https://data.geopf.fr/wmts?" +
          "&REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=PM" +
          "&LAYER={ignLayer}&STYLE={style}&FORMAT={format}" +
          "&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}",
        {
          ignLayer: "CADASTRALPARCELS.PARCELLAIRE_EXPRESS",
          style: "PCI vecteur",
          format: "image/png",
          service: "WMTS",
          attribution:
            '&copy; <a href="http://www.ign.fr/" target="_blank">IGN</a>, &copy; <a href="http://raid.bde-insa-lyon.fr/" target="_blank">Raid INSA Lyon - Orange</a>',
        }
      );

      let baseMaps = {
        "Carte IGN": carteIGN,
        "Plan IGN": planIGN,
        Satellite: photosAeriennes,
      };

      let overlayMaps = {
        "Natura2000 Habitats": natura2000Habitats,
        "Natura2000 Oiseaux": natura2000Oiseaux,
        Cadastre: cadastre,
      };

      var mymap = L.map("leaflet-map", {
        center: [45.783249, 4.878094],
        zoom: 15,
        zoomControl: false,
        zoomDelta: 0.25,
        zoomSnap: 0.25,
        layers: [planIGN],
        editable: true,
      });

      var layerControl = L.control.layers(baseMaps, overlayMaps, {
        collapsed: true,
        position: "topright",
      });

      var zoomControl = L.control.zoom({
        position: "topright",
      });

      L.Control.Location = L.Control.extend({
        old_marker: null,
        old_circle: null,
        onAdd: function (map) {
          var btn = L.DomUtil.create("button");
          btn.classList = "btn";
          btn.style = "background-color: #fff; border-color: #ccc;";
          btn.innerHTML = '<i class="fas fa-location-arrow"></i>';

          btn.onclick = () => {
            function onLocationFound(e) {
              if (this.old_marker != null) {
                this.old_marker.removeFrom(mymap);
              }
              if (this.old_circle != null) {
                this.old_circle.removeFrom(mymap);
              }

              var radius = e.accuracy / 2;

              this.old_marker = L.marker(e.latlng)
                .addTo(map)
                .bindPopup(
                  "Votre position à " + new Date().toLocaleTimeString()
                )
                .openPopup();
              setTimeout(() => {
                this.old_marker.closePopup();
              }, 2500);

              this.old_circle = L.circle(e.latlng, radius).addTo(map);
            }

            function onLocationError(e) {
              alert(e.message);
            }

            mymap.on("locationfound", onLocationFound);
            mymap.on("locationerror", onLocationError);

            mymap.locate({ setView: true });
          };
          return btn;
        },

        onRemove: function (map) {
          // Nothing to do here
        },
      });

      L.control.location = function (opts) {
        return new L.Control.Location(opts);
      };

      L.control.location({ position: "topright" }).addTo(mymap);

      this.elevationControl = L.control.elevation({
        position: "bottomright",
        collapsed: true,
        autohide: false,
        detached: false,
        theme: "orange-elevation-theme",
        legend: false,
      });

      this.sidebar = L.control
        .sidebar({
          autopan: false, // whether to maintain the centered map point when opening the sidebar
          closeButton: true, // whether t add a close button to the panes
          container: "sidebar", // the DOM container or #ID of a predefined sidebar container that should be used
          position: "left", // left or right
        })
        .addTo(mymap);

      L.control
        .scale({ imperial: false, position: "bottomright" })
        .addTo(mymap);

      layerControl.addTo(mymap);
      zoomControl.addTo(mymap);
      this.elevationControl.addTo(mymap);

      this.polylineTooltip = L.DomUtil.get("polyline-tooltip");

      this.map = mymap;
    },
    initLayers() {
      this.layers.forEach((layer) => {
        layer.openraideur_waypoint_features.forEach((feature) => {
          this.$set(feature, "type", "waypoint");
          this.$set(feature, "editState", false);
        });

        layer.openraideur_race_features.forEach((feature) => {
          this.$set(feature, "type", "race");
          this.$set(feature, "editState", false);
        });
      });
    },
    getMarkerIcon(icon, color) {
      return L.divIcon({
        className: "marker-icon",
        html: `<svg style="display: block; width:100%; height:100%; margin: 0px; padding: 0px;">
            <path fill="${color}" d="M16,1 C7.7146,1 1,7.65636364 1,15.8648485 C1,24.0760606 16,51 16,51 C16,51 31,24.0760606 31,15.8648485 C31,7.65636364 24.2815,1 16,1 L16,1 Z" fill-opacity="0.8" stroke=#eee stroke-width="2"></path>
          </svg>
          <div style="position: absolute; left: 20%; top: 17%; z-index: 1000 padding: 0px;">
            <i class="fas fa-fw fa-lg fa-${icon}"></i>
          </div>`,
        iconSize: [34, 50],
        iconAnchor: [17, 50],
        popupAnchor: [0, -40],
      });
    },
    onGPXFileChoose() {
      if (this.gpxFile != null) {
        let reader = new FileReader();
        reader.onload = (e) => {
          if (e.target.result) {
            let parser = new DOMParser();
            let xmlDoc = parser.parseFromString(e.target.result, "text/xml");

            let trk = xmlDoc.getElementsByTagName("trk")[0];
            let wpt = xmlDoc.getElementsByTagName("wpt")[0];
            if (trk != undefined) {
              //c'est un gpx de parcours
              if (trk.getElementsByTagName("trkseg").length == 1) {
                let track = [];
                for (let pt of trk.getElementsByTagName("trkpt")) {
                  let lat = +pt.getAttribute("lat");
                  let lon = +pt.getAttribute("lon");
                  let ele = +pt.getElementsByTagName("ele")[0].textContent;
                  track.push([lat, lon, ele]);
                }

                this.newFeature = {
                  name: trk.getElementsByTagName("name")[0].textContent,
                  layer: null,
                  color: "#FF0000",
                  leafletObject: L.polyline(track, { color: "red" }).addTo(
                    this.map
                  ),
                };
                this.$root.$emit("bv::toggle::collapse", "accordion-add-race");
              } else {
                this.showToast(
                  "danger",
                  "Erreur d'importation du GPX",
                  "Le fichier GPX choisi contient plusieurs parcours, et cette fonctionnalité n'est pas supportée pour le moment."
                );
              }
            } else if (wpt != undefined) {
              //c'est un gpx de waypoint
              let lat = +wpt.getAttribute("lat");
              let lon = +wpt.getAttribute("lon");

              this.newFeature = {
                name: wpt.getElementsByTagName("name")[0].textContent,
                layer: null,
                color: "#2e84cb",
                leafletObject: L.marker([lat, lon]).addTo(this.map),
              };
              this.$root.$emit("bv::toggle::collapse", "accordion-add-marker");
            } else {
              this.showToast(
                "danger",
                "Erreur d'importation du GPX",
                "Le fichier GPX ne semble contenir ni un marqueur, ni un parcours. Réessayez avec un fichier GPX valide."
              );
            }
          }
        };
        reader.readAsText(this.gpxFile);
      }
    },
    onKMLFileChoose() {
      if (this.kmlFile != null) {
        let reader = new FileReader();
        reader.onload = (e) => {
          if (e.target.result) {
            let parser = new DOMParser();
            let xmlDoc = parser.parseFromString(e.target.result, "text/xml");

            let dayName = xmlDoc.getElementsByTagName("name")[0].textContent;
            let kml_layers = xmlDoc.getElementsByTagName("Folder");

            let layers = [];
            for (let kml_layer of kml_layers) {
              let layer_name = kml_layer.getElementsByTagName("name")[0].textContent;
              let kml_features = kml_layer.getElementsByTagName("Placemark")
              let races = [];
              let waypoints = [];
              for (let kml_feature of kml_features) {
                let feature_name = kml_feature.getElementsByTagName("name")[0].textContent;
                let kml_feature_description = kml_feature.getElementsByTagName("description")[0];
                let feature_description = "";
                if (kml_feature_description != undefined) {
                  feature_description = kml_feature_description.textContent.replace('<br>', '\n');
                }
                let kml_waypoint_point = kml_feature.getElementsByTagName("Point");
                let kml_race_coordinates_linsestring = kml_feature.getElementsByTagName("LineString");

                if (kml_waypoint_point.length == 1) {
                  let kml_waypoint_coordinates = kml_waypoint_point[0].getElementsByTagName('coordinates')[0].textContent;
                  let waypoint = {
                    name: feature_name,
                    comment: feature_description,
                    lon: Number(kml_waypoint_coordinates.split(',')[0]),
                    lat: Number(kml_waypoint_coordinates.split(',')[1])
                  }
                  waypoints.push(waypoint);
                }
                else if (kml_race_coordinates_linsestring.length == 1) {
                  let kml_race_coordinates = kml_race_coordinates_linsestring[0].getElementsByTagName('coordinates')[0].textContent;
                  let race = {
                    name: feature_name,
                    comment: feature_description,
                    track: kml_race_coordinates.split('\n').map(pt => {
                      let [lng, lat, alt] = pt.split(',').map(n => Number(n));
                      if (lng && lat) {
                        if (alt == undefined || alt == 0) {
                          return [lat, lng];
                        }
                        else {
                          return [lat, lng, alt];
                        }
                      }
                      else {
                        return null;
                      }
                    }).filter(e => e != null)
                  }
                  races.push(race);
                }
              }
              let layer = {
                name: layer_name,
                races,
                waypoints
              };
              layers.push(layer);
            }

            let days_data = this.days.map(d => {
              return d.id + " : " + d.name;
            }).join('\n');
            let dayId = Number(prompt("Saisir l'ID du jour sur lequel importer les éléments du KML (jour suggéré dans le KML : " + dayName + ") :\n" + days_data));
            
            if (dayId) {
              for (let layer of layers) {
                let newLayer = {
                  day: dayId,
                  name: layer.name
                };
                
                this.saveNewLayer(newLayer).then((newLayer => {
                  if (newLayer) {
                    for (let race of layer.races) {
                      let newRace = {
                        name: race.name,
                        comment: race.comment,
                        color: '#FF0000',
                        layer: newLayer.id,
                        track: race.track
                      };
                      this.saveNewRace(newRace).then(newRace => {
                        if (!newRace) {
                          console.log("Erreur d'importation Race");
                          console.log(race);
                        }
                      });
                    }

                    for (let waypoint of layer.waypoints) {
                      let newWaypoint = {
                        name: waypoint.name,
                        comment: waypoint.comment,
                        color: '#FF0000',
                        icon: 'dot-circle',
                        layer: newLayer.id,
                        lat: waypoint.lat,
                        lon: waypoint.lon
                      };
                      this.saveNewWaypoint(newWaypoint).then(newWaypoint => {
                        if (!newWaypoint) {
                          console.log("Erreur d'importation Waypoint");
                          console.log(waypoint);
                        }
                      });
                    }
                  }
                  else {
                    console.log("Erreur d'importation Layer");
                    console.log(layer);
                  } 
                }));
              }

              this.showToast(
                "success",
                "Importation du KML en cours",
                "Vérifiez la console pour connaitre le résultat"
              );
            }
          }
        };
        reader.readAsText(this.kmlFile);
      }
    },
    onLayerVisibilityChanged(layerId, active) {
      const layer = this.layers.find((layer) => layer.id === layerId);

      layer.openraideur_waypoint_features.forEach((feature) => {
        if (active) {
          if (!feature.leafletObject) {
            var markerIcon = this.getMarkerIcon(feature.icon, feature.color);
            feature.leafletObject = L.marker([feature.lat, feature.lon], {
              icon: markerIcon,
            }).bindPopup(feature.name);
          }
          feature.leafletObject.addTo(this.map);
        } else {
          if (feature.leafletObject) {
            feature.leafletObject.removeFrom(this.map);
            feature.leafletObject = null;
          }
        }
      });
      layer.openraideur_race_features.forEach(async (feature) => {
        if (active) {
          if (!feature.leafletObject) {
            await this.getRaceTrack(feature);
            feature.leafletObject = new L.Polyline(feature.track, {
              color: feature.color,
              opacity: 0.85,
            }).bindPopup(feature.name);
          }
          feature.leafletObject.addTo(this.map);
        } else {
          if (feature.leafletObject) {
            feature.leafletObject.removeFrom(this.map);
            feature.leafletObject = null;
          }
        }
      });
    },
    onFeatureSelected(feature) {
      this.selectedFeature = feature;
    },
    modalFeatureChanged(changedFields) {
      for (let f in changedFields) {
        this.selectedFeature[f] = changedFields[f];
      }
      this.toggleSelectedFeatureEditState(false);
      this.showSaveSuccessToast(this.selectedFeature);
    },
    toggleSelectedFeatureEditState(newState) {
      let feature = this.selectedFeature;
      if (newState != feature.editState) {
        if (feature.type == "race") {
          if (feature.editState) {
            feature.leafletObject.remove(this.map);
            feature.leafletObject = new L.Polyline(feature.track, {
              color: feature.color,
              opacity: 0.85,
            }).bindPopup(feature.name);
            feature.leafletObject.addTo(this.map);
          } else {
            feature.leafletObject.remove(this.map);
            feature.leafletObject = L.Polyline.PolylineEditor(
              feature.leafletObject.getLatLngs(),
              {
                maxMarkers: 100,
                pointIcon: L.icon({
                  iconUrl: require("@/assets/editmarker.png"),
                  iconSize: [11, 11],
                  iconAnchor: [6, 6],
                }),
                newPointIcon: L.icon({
                  iconUrl: require("@/assets/editmarker2.png"),
                  iconSize: [11, 11],
                  iconAnchor: [6, 6],
                }),
                color: feature.color,
              }
            ).addTo(this.map);
          }
        } else if (feature.type == "waypoint") {
          if (feature.editState) {
            feature.leafletObject.disableEdit();
            feature.leafletObject.edited = false;
          } else {
            feature.leafletObject.enableEdit();
          }
        }
        feature.editState = newState;
      }
    },
    modalSaveError(feature, error) {
      this.showSaveErrorToast(feature, error);
    },
    modalDeleteSuccess(feature) {
      let feature_name = feature.name;

      if (feature.leafletObject != null) {
        feature.leafletObject.removeFrom(this.map);
        feature.leafletObject = null;
      }

      //remove elmt from this.layers
      let layer_index = this.layers.findIndex((l) => l.id == feature.layer);

      if (layer_index > -1) {
        let feature_array = undefined;

        if (feature.type == "race") {
          feature_array = this.layers[layer_index].openraideur_race_features;
        } else if (feature.type == "waypoint") {
          feature_array = this.layers[layer_index]
            .openraideur_waypoint_features;
        }

        let feature_index = feature_array.findIndex((f) => f.id == feature.id);
        if (feature_index > -1) {
          feature_array.splice(feature_index, 1);
        }
      }

      this.showToast(
        "success",
        "Elément supprimé",
        feature_name + " a été supprimé avec succès."
      );
    },
    modalDeleteError(feature, error) {
      this.showToast(
        "danger",
        "Erreur de suppression",
        feature.title + " n'a pas pu être supprimé : " + error
      );
    },
    onFlyToFeatureClick(feature) {
      if (feature.type == "waypoint") {
        this.map.flyTo(feature.leafletObject.getLatLng(), 15);
      } else if (feature.type == "race") {
        if (feature.leafletObject != null) {
          this.map.fitBounds(feature.leafletObject.getBounds());

          this.elevationControl.clear();
          this.elevationControl.loadData({
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: this.reverseCoords(feature.track),
                },
              },
            ],
          });
        }
      }
    },
    createAntPath(feature) {
      return new AntPath(feature.track, {
        color: feature.color,
        pulseColor: "#111",
        opacity: 0.85,
        delay: 2000,
        hardwareAccelerated: true,
        dashArray: [10, 40],
      }).bindPopup(feature.name);
    },
    onToggleAntRace(feature, enabled) {
      if (feature.leafletObject != null && feature.editState != true) {
        if (enabled) {
          this.antRace = this.createAntPath(feature);
          this.antRace.addTo(this.map);
        } else {
          this.antRace.removeFrom(this.map);
          this.antRace = null;
        }
      }
    },
    onRaceAddStarted() {
      if (this.newFeature.leafletObject == null) {
        this.newFeature.leafletObject = this.map.editTools.startPolyline(null, {
          color: "red",
        });
        this.addPolylineTooltip();
      }
    },
    onRaceAddCanceled() {
      if (this.newFeature.leafletObject != null) {
        this.newFeature.leafletObject.disableEdit();
        this.removePolylineTooltip();
      }
      this.resetNewFeature();
    },
    onNewRaceSave(e) {
      e.preventDefault();

      if (this.newFeature.leafletObject.getLatLngs().length >= 2) {
        if (this.newFeature.leafletObject.editEnabled()) {
          this.newFeature.leafletObject.disableEdit();
        }
        
        let newRace = {
          name: this.newFeature.name,
          color: this.newFeature.color,
          layer: this.newFeature.layer,
          track: this.getSlimCords(
            this.newFeature.leafletObject.getLatLngs()
          )
        }
        this.saveNewRace(newRace).then((newRace) => {
          if (newRace) {
            this.$root.$emit("bv::toggle::collapse", "accordion-add-race");
            this.showSaveSuccessToast(newRace);
            this.resetNewFeature();
          }
        });
      } else {
        this.showToast(
          "info",
          "Information",
          "Tracez un parcours avant de le sauvegarder."
        );
      }
    },
    saveNewRace(newRace) {
      return axios
        .post("/api/race/", newRace)
        .then((response) => {
          var feature = response.data;
          var layer = this.layers.find((l) => l.id == feature.layer);
          feature.leafletObject = new L.Polyline(feature.track, {
            color: feature.color,
            opacity: 0.85,
          }).bindPopup(feature.name);
          feature.type = "race";
          layer.openraideur_race_features.push(feature);
          layer.openraideur_race_features.sort((f1, f2) =>
            f1.name > f2.name ? 1 : -1
          );
          this.onLayerVisibilityChanged(layer.id, true);
          layer.active = true;
          return feature;
        })
        .catch((error) => {
          this.showSaveErrorToast(this.newFeature, error);
          return null;
        });
    },
    addPolylineTooltip() {
      this.map.on("mousemove", this.moveTooltip);
      this.map.on("editable:drawing:end", this.removePolylineTooltip);
      this.map.on("editable:editing", this.updatePolylineDistance);
      this.polylineTooltip.innerHTML = "~0 km";
      this.polylineTooltip.style.display = "block";
    },
    removePolylineTooltip() {
      this.polylineTooltip.innerHTML = "~0 km";
      this.polylineTooltip.style.display = "none";
      this.map.off("mousemove", this.moveTooltip);
      this.map.off("editable:drawing:end", this.removePolylineTooltip);
      this.map.off("editable:editing", this.updatePolylineDistance);
    },
    moveTooltip(e) {
      this.polylineTooltip.style.left = e.originalEvent.clientX + 20 + "px";
      this.polylineTooltip.style.top = e.originalEvent.clientY - 10 + "px";

      let latlngs = this.newFeature.leafletObject.getLatLngs();
      let additionnalDistance = 0;
      if (latlngs.length != 0) {
        let lastLatLng = latlngs[latlngs.length - 1];
        additionnalDistance = Math.round(lastLatLng.distanceTo(e.latlng));
      }
      this.polylineTooltip.innerHTML =
        "~" +
        (this.newFeature.drawnDistance + additionnalDistance) / 1000 +
        " km";
    },
    updatePolylineDistance(e) {
      let distance = 0;
      let latlngs = e.layer.getLatLngs();
      for (let i = 0; i < latlngs.length - 1; i++) {
        distance += latlngs[i].distanceTo(latlngs[i + 1]);
      }
      this.newFeature.drawnDistance = Math.round(distance);
    },
    onWaypointAddStarted() {
      if (this.newFeature.leafletObject == null) {
        this.newFeature.leafletObject = this.map.editTools.startMarker(null, {
          color: "#2e84cb",
        });
      }
    },
    onWaypointAddCanceled() {
      if (this.newFeature.leafletObject != null) {
        this.newFeature.leafletObject.disableEdit();
      }
      this.resetNewFeature();
    },
    onNewWaypointSave(e) {
      e.preventDefault();

      if (this.newFeature.leafletObject.editEnabled()) {
        this.newFeature.leafletObject.disableEdit();
      }
      
      let newWaypoint = {
        name: this.newFeature.name,
        color: this.newFeature.color,
        layer: this.newFeature.layer,
        icon: this.newFeature.icon,
        lat: this.newFeature.leafletObject.getLatLng().lat,
        lon: this.newFeature.leafletObject.getLatLng().lng
      }

      this.saveNewWaypoint(newWaypoint).then((newWaypoint) => {
        if (newWaypoint) {
          this.$root.$emit("bv::toggle::collapse", "accordion-add-marker");
          this.showSaveSuccessToast(newWaypoint);
          this.resetNewFeature();
        }
      });
    },
    saveNewWaypoint(newWaypoint) {
      return axios
        .post("/api/waypoint/", newWaypoint)
        .then((response) => {
          var feature = response.data;
          var layer = this.layers.find((l) => l.id == feature.layer);

          var markerIcon = this.getMarkerIcon(feature.icon, feature.color);
          feature.leafletObject = L.marker([feature.lat, feature.lon], {
            icon: markerIcon,
          }).bindPopup(feature.name);
          feature.leafletObject.addTo(this.map);
          feature.type = "waypoint";
          layer.openraideur_waypoint_features.push(feature);
          layer.openraideur_waypoint_features.sort((f1, f2) =>
            f1.name > f2.name ? 1 : -1
          );
          this.onLayerVisibilityChanged(layer.id, true);
          layer.active = true;
          return feature;
        })
        .catch((error) => {
          this.showSaveErrorToast(this.newFeature, error);
          return null;
        });
    },
    resetNewFeature() {
      if (this.newFeature.leafletObject != null) {
        this.newFeature.leafletObject.removeFrom(this.map);
      }

      this.newFeature = {
        name: "",
        color: "#FF0000",
        leafletObject: null,
        drawnDistance: 0,
        additionnalDistance: 0,
      };
    },
    onNewLayerSave(e) {
      e.preventDefault();
      let newLayer = {
        name: this.newLayer.name,
        day: this.newLayer.day,
      }
      
      this.saveNewLayer(newLayer).then((newLayer) => {
        if (newLayer) {
          this.$root.$emit("bv::toggle::collapse", "accordion-add-layer");
          this.showSaveSuccessToast(newLayer);
        }
      });
    },
    saveNewLayer(newLayer) {
      return axios
        .post("/api/layer/", newLayer)
        .then((response) => {
          var layer = response.data;
          this.layers.push(layer);
          return layer;
        })
        .catch((error) => {
          this.showSaveErrorToast(this.newLayer, error);
          return null;
        });
    },
    getPolylineCoords(polylineFeature) {
      return this.reverseCoords(
        polylineFeature.toGeoJSON().geometry.coordinates
      );
    },
    getSlimCords(fatCoords) {
      let coords = [];
      for (let c of fatCoords) {
        coords.push([c.lat, c.lng]);
      }
      return coords;
    },
    reverseCoords(initialCoords) {
      let coords = [];
      for (var i = 0; i < initialCoords.length; i++) {
        var [lng, lat, alt] = initialCoords[i];
        if (alt == null) {
          coords[i] = [lat, lng];
        } else {
          coords[i] = [lat, lng, alt];
        }
      }
      return coords;
    },
    downloadKML(dayName, dayId) {
      axios.get('/api/day/' + dayId + "/kml/")
      .then(response => {
          var element = document.createElement('a');
          element.setAttribute('href', 'data:application/vnd.google-earth.kml+xml;charset=utf-8,' + encodeURIComponent(response.data));
          element.setAttribute('download', dayName + '_' + this.user.year + ".kml");

          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
      })
      .catch(error => {
          console.log(error);
      })
    },
    logout() {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("username");
      localStorage.removeItem("year");
      localStorage.removeItem("external_user");

      this.$router.go();
    },
  },
};
</script>

<style>
#leaflet-map {
  height: 95vh;
  width: 100%;
}

.orange-elevation-theme.leaflet-control.elevation .area {
  fill: rgb(255, 121, 0, 0.75);
  stroke: rgba(0, 0, 0, 0.9);
  stroke-width: 2;
}

.orange-elevation-theme.height-focus.circle-lower {
  fill: rgb(255, 121, 0);
}

.orange-elevation-theme.elevation-polyline {
  stroke-opacity: 0;
}

#sidebar {
  font-size: 16px !important;
}

.marker-icon {
  background-color: transparent;
}

#polyline-tooltip {
  display: none;
  position: absolute;
  background: #444;
  color: white;
  opacity: 0.7;
  padding: 0 10px 0 10px;
  border: 1px dashed #999;
  font-family: sans-serif;
  font-size: 12px;
  height: 20px;
  z-index: 1000;
}
</style>
